import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, BehaviorSubject, filter, takeUntil } from 'rxjs';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { 
  AccountInfo, 
  AuthenticationResult, 
  EventMessage, 
  EventType, 
  InteractionStatus,
  RedirectRequest,
  SilentRequest
} from '@azure/msal-browser';
import { loginRequest } from '../auth/msal.config';
import { environment } from '../../environments/environment';
import { TruthManagement } from './truth-management';
import { ExtractionRunService } from './extraction-run.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly _destroying$ = new Subject<void>();

  // Single source of truth for user role — null = not yet determined
  private userRoleSubject = new BehaviorSubject<string | null>(null);
  public userRole$ = this.userRoleSubject.asObservable();

  // Client management
  private assignedClientsSubject = new BehaviorSubject<Array<{client_id: string; client_name: string; module?: string}>>([]);
  public assignedClients$ = this.assignedClientsSubject.asObservable();

  private currentClientSubject = new BehaviorSubject<{client_id: string; client_name: string; module?: string} | null>(null);
  public currentClient$ = this.currentClientSubject.asObservable();

  // True until the role/clients fetch resolves (or is determined unnecessary) — lets the
  // header show a loader instead of "No Clients" while the assigned client is still unknown.
  private isLoadingClientsSubject = new BehaviorSubject<boolean>(true);
  public isLoadingClients$ = this.isLoadingClientsSubject.asObservable();

  // Deduplication flag — prevents concurrent role API calls
  private isFetchingRole = false;

  // Current logged in account
  private _account: AccountInfo | null = null;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private truthManagementService: TruthManagement,
    private extractionRunService: ExtractionRunService,
    private router: Router
  ) {
    // Seed role from valid sessionStorage cache immediately (synchronous)
    // so components and guards see a value before any async API call completes
    const cachedRole = sessionStorage.getItem('userRole');
    if (cachedRole) {
      this.userRoleSubject.next(cachedRole);
    }

    // Restore clients and current client from localStorage
    this.restoreClientsFromCache();

    // If the role is already cached, setActiveAccount() below will skip fetching it (and the
    // clients that come with it), so there is nothing left to wait on — stop loading now.
    if (cachedRole) {
      this.isLoadingClientsSubject.next(false);
    }

    this.initializeMsal();
  }

  /**
   * Initialize MSAL and set up event listeners
   */
  private initializeMsal(): void {
    // Note: handleRedirectObservable() is called in app.ts to avoid duplicate calls
    // Just listen for events here

    // Listen for login success events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this._account = payload.account;
        
        // Fetch and cache user role immediately after login
        // (fetchAndCacheUserRole will handle cache management internally)
        this.fetchAndCacheUserRole();
      });

    // Listen for interaction status changes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setActiveAccount();
      });

    // Set initial active account
    this.setActiveAccount();
  }

  /**
   * Set the active account from all accounts
   */
  private setActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    
    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
      this._account = accounts[0];
      // Only fetch role if not already cached for this session
      if (!sessionStorage.getItem('userRole')) {
        this.fetchAndCacheUserRole();
      }
    } else if (activeAccount) {
      this._account = activeAccount;
      // Only fetch role if not already cached for this session
      if (!sessionStorage.getItem('userRole')) {
        this.fetchAndCacheUserRole();
      }
    }
  }

  /**
   * Normalize role: convert to lowercase and remove all whitespace characters.
   * Matches backend behavior in normalize_role() from jwtauth.py
   * Examples: 'Admin ' → 'admin', 'Super User' → 'superuser', 'READ-ONLY-USER' → 'read-only-user'
   */
  private normalizeRole(role: string | null | undefined): string {
    if (!role) {
      return '';
    }
    // Convert to lowercase and remove all whitespace characters
    return role.toLowerCase().split(/\s+/).join('');
  }

  /**
   * Returns the current role as a synchronous string — safe for templates and guards.
   * Falls back to sessionStorage when BehaviorSubject is not yet seeded.
   * Role is always normalized (lowercase, no whitespace).
   */
  get currentRole(): string {
    return this.userRoleSubject.getValue() ?? sessionStorage.getItem('userRole') ?? '';
  }

  /**
   * Returns the current user's email address from sessionStorage.
   * Available immediately after authentication, regardless of role API success/failure.
   * Safe for use in templates and components for displaying user identity (tooltips, headers, etc.)
   */
  get currentUserEmail(): string {
    return sessionStorage.getItem('userEmail') ?? '';
  }

  /**
   * Returns the current user's role from sessionStorage for display purposes.
   * Always reflects the actual role stored, whether from API or default fallback (read-only-user).
   * Use with formatRoleForDisplay() to get human-readable labels for UI.
   */
  get currentUserRole(): string {
    return this.currentRole;
  }

  /**
   * Formats user role for display in UI (tooltips, headers, user profile, etc.)
   * Converts database role values to human-readable labels.
   * Process: normalize role (lowercase, remove all whitespace) → lookup display label
   * 
   * Examples:
   *   'admin' → 'Admin'
   *   'superuser' → 'Superuser'
   *   'read-only-user' → 'Read-Only User'
   *   'user' → 'User'
   */
  formatRoleForDisplay(role?: string): string {
    const roleValue = role || this.currentUserRole;
    
    if (!roleValue) return '';

    // Normalize role using same logic as backend (lowercase + remove whitespace)
    const normalized = this.normalizeRole(roleValue);

    // Map of database role values to display labels (keys must be normalized)
    const roleDisplayMap: Record<string, string> = {
      'admin': 'Admin',
      'superuser': 'Superuser',
      'read-only-user': 'Read Only',
      'user': 'User'
    };

    return roleDisplayMap[normalized] || roleValue;
  }

  /**
   * Get user email from sessionStorage
   */
  getUserEmail(): string {
    return sessionStorage.getItem('userEmail') || '';
  }

  /**
   * Get username from email (part before @)
   */
  getUsernameFromEmail(email: string = ''): string {
    const emailToUse = email || this.getUserEmail();
    return emailToUse.split('@')[0];
  }

  /**
   * Get formatted role and email for header tooltip.
   * Returns format: "Role - email@example.com"
   */
  getUserRoleAndEmail(): string {
    const role = this.formatRoleForDisplay();
    const email = this.getUserEmail();
    return role && email ? `${role} - ${email}` : email || 'User';
  }

  /** Synchronous admin check — delegates to currentRole (normalized). */
  get isAdmin(): boolean {
    const role = this.normalizeRole(this.currentRole);
    return role === 'admin' || role === 'superuser';
  }

  /** Synchronous superuser check — delegates to currentRole (normalized). */
  get isSuperUser(): boolean {
    return this.normalizeRole(this.currentRole) === 'superuser';
  }

  /** Synchronous read-only user check — delegates to currentRole (normalized). */
  get isReadOnly(): boolean {
    const role = this.normalizeRole(this.currentRole);
    return role === 'read-only-user';
  }
  /**
   * Fetch user role from API and cache it in sessionStorage for the session duration.
   *
   * Called only when no cached role exists in sessionStorage:
   * - After fresh login (LOGIN_SUCCESS event)
   * - On first-ever page load before any role has been determined
   *
   * On page refresh, sessionStorage already has the role so setActiveAccount() skips
   * this call entirely. The cache is cleared on logout, ensuring the next login always
   * fetches a fresh value from the API.
   *
   * isFetchingRole deduplicates concurrent calls during the brief race window on first
   * login when MSAL fires setActiveAccount() before sessionStorage has been written.
   */
  private fetchAndCacheUserRole(): void {
    // Deduplication: one API call per page load regardless of how many times
    // setActiveAccount() is triggered by MSAL lifecycle events
    if (this.isFetchingRole) {
      return;
    }

    this.isLoadingClientsSubject.next(true);

    const account = this.getActiveAccount();
    if (!account) {
      this.isLoadingClientsSubject.next(false);
      return;
    }

    // Extract email — try multiple locations Azure B2C may store it
    const accountAny = account as any;
    let userEmail: string | undefined;

    if (accountAny.idTokenClaims) {
      userEmail = accountAny.idTokenClaims.email ||
                  accountAny.idTokenClaims.preferred_username ||
                  accountAny.idTokenClaims.emails?.[0] ||
                  accountAny.idTokenClaims.upn;
    }
    if (!userEmail) {
      userEmail = account.username || accountAny.email || accountAny.loginHint || account.name;
    }
    if (!userEmail || userEmail.trim() === '') {
      this.isLoadingClientsSubject.next(false);
      return;
    }

    // Store user email and session ID immediately (independent of role API)
    // This ensures email is always available for display (tooltips, headers, etc.)
    sessionStorage.setItem('userEmail', userEmail);
    sessionStorage.setItem('userSessionId', accountAny.homeAccountId);
    this.isFetchingRole = true;
    this.truthManagementService.getUserRole(userEmail).subscribe({
      next: (response) => {
        // Check if API returned an error status in the response body
        if (response.status === 'FAILED' || response.status === 'ERROR') {
          const defaultRole = 'read-only-user';
          this.userRoleSubject.next(defaultRole);
          sessionStorage.setItem('userRole', defaultRole);
          // Clear clients since API failed
          this.assignedClientsSubject.next([]);
          this.currentClientSubject.next(null);
          this.isFetchingRole = false;
          this.isLoadingClientsSubject.next(false);
          return;
        }

        // Normalize role: convert to lowercase and remove all whitespace (matches backend)
        const normalized = this.normalizeRole(response.role || 'read-only-user');
        sessionStorage.setItem('userRole', normalized);
        this.userRoleSubject.next(normalized);

        // Extract and cache clients from response
        if (response.clients && Array.isArray(response.clients)) {
          const sortedClients = response.clients.sort((a, b) => 
            a.client_name.localeCompare(b.client_name)
          );
          this.assignedClientsSubject.next(sortedClients);
          
          // Persist clients to sessionStorage
          try {
            sessionStorage.setItem('assignedClients', JSON.stringify(sortedClients));
          } catch (err) {
          }

          // Set current client: restore from localStorage if available, otherwise use first
          this.initializeCurrentClient(sortedClients);
        } else {
          this.assignedClientsSubject.next([]);
          this.currentClientSubject.next(null);
        }

        this.isFetchingRole = false;
        this.isLoadingClientsSubject.next(false);
      },
      error: (error) => {
        // User not found in DB (404) or other API error: default to read-only-user
        // This ensures users not in the system get restricted access by default
        this.userRoleSubject.next('read-only-user');
        sessionStorage.setItem('userRole', 'read-only-user');
        this.assignedClientsSubject.next([]);
        this.currentClientSubject.next(null);
        this.isFetchingRole = false;
        this.isLoadingClientsSubject.next(false);
      }
    });
  }

  /**
   * Initiate login via redirect to Azure AD B2C
   */
  login(): void {
    const authRequest: RedirectRequest = {
      ...this.msalGuardConfig.authRequest as RedirectRequest,
      scopes: loginRequest.scopes,
      prompt: 'select_account'  // Always show account picker on login
    };
    this.msalService.loginRedirect(authRequest);
  }

  /**
   * Logout and clear all cached tokens
   */
  logout(): void {
    // Reset transient role state so a fresh login starts clean.
    this.userRoleSubject.next(null);
    this.isFetchingRole = false;

    // Clear role cache from session storage
    sessionStorage.removeItem('userRole');
    sessionStorage.removeItem('userEmail');
    sessionStorage.removeItem('userSessionId');
    // Clear client cache from session storage
    sessionStorage.removeItem('assignedClients');
    sessionStorage.removeItem('currentClient');
    this.assignedClientsSubject.next([]);
    this.currentClientSubject.next(null);
    // Clear stats dashboard caches (filters and completed runs cache)
    sessionStorage.removeItem('stats-dashboard-filters');
    sessionStorage.removeItem('stats-completed-runs-cache');
    // Clear active evaluations from sessionStorage so a subsequent login by a
    // different user never sees stale active-run state from this session.
    sessionStorage.removeItem('activeEvaluations');

    // Clear extraction runs from sessionStorage and memory
    this.extractionRunService.clearRuns();

    // Clear all in-memory service caches (eval runs, status, document keys, etc.)
    this.truthManagementService.clearAllCaches();

    // Get all accounts and clear them to prevent account picker
    const accounts = this.msalService.instance.getAllAccounts();
    // Clear all accounts from cache to prevent account picker on next logout
    accounts.forEach(account => {
      this.msalService.instance.setActiveAccount(null);
    });

    // Use logoutRedirect for cleaner logout experience
    this.msalService.logoutRedirect({
      onRedirectNavigate: () => {
        // After redirect, navigate to login page
        this.router.navigate(['/login']);
        return false; // Prevent MSAL from navigating
      }
    });
  }

  /**
   * Check if user is currently authenticated
   */
  get isAuthenticated(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * Alias for isAuthenticated() - for backward compatibility
   */
  hasValidToken(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get the current active account
   */
  getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Get all accounts
   */
  getAllAccounts(): AccountInfo[] {
    return this.msalService.instance.getAllAccounts();
  }

  // ============================================
  // CLIENT MANAGEMENT METHODS
  // ============================================

  /**
   * Get assigned clients for the current user, sorted alphabetically by client_name
   */
  getAssignedClients(): Array<{client_id: string; client_name: string; module?: string}> {
    return this.assignedClientsSubject.getValue();
  }

  /**
   * Get the currently selected client
   */
  getCurrentClient(): {client_id: string; client_name: string; module?: string} | null {
    return this.currentClientSubject.getValue();
  }

  getCurrentClientModule(): string {
    return this.currentClientSubject.getValue()?.module || 'Loan Origination';
  }

  /**
   * Set the current client and persist to sessionStorage
   */
  setCurrentClient(clientId: string): void {
    const clients = this.getAssignedClients();
    const selectedClient = clients.find(c => c.client_id === clientId);
    
    if (selectedClient) {
      this.currentClientSubject.next(selectedClient);
      try {
        sessionStorage.setItem('currentClient', JSON.stringify(selectedClient));
      } catch (err) {
      }
    } else {
    }
  }

  /**
   * Restore clients from sessionStorage cache and initialize current client
   * Called on service init (before role API call)
   */
  private restoreClientsFromCache(): void {
    try {
      const cachedClientsStr = sessionStorage.getItem('assignedClients');
      if (cachedClientsStr) {
        const cachedClients = JSON.parse(cachedClientsStr);
        if (Array.isArray(cachedClients) && cachedClients.length > 0) {
          this.assignedClientsSubject.next(cachedClients);
          // Try to restore previously selected client
          const currentClientStr = sessionStorage.getItem('currentClient');
          if (currentClientStr) {
            const currentClient = JSON.parse(currentClientStr);
            // Verify the restored client still exists in the assigned clients list
            if (cachedClients.find((c: any) => c.client_id === currentClient.client_id)) {
              this.currentClientSubject.next(currentClient);
            } else {
              // Restored client no longer assigned, use first one
              this.currentClientSubject.next(cachedClients[0]);
            }
          } else {
            // No previously selected client, use first one
            this.currentClientSubject.next(cachedClients[0]);
          }
        }
      }
    } catch (err) {
    }
  }

  /**
   * Initialize current client from sorted clients list
   * Called after role API response with fresh client list
   */
  private initializeCurrentClient(sortedClients: Array<{client_id: string; client_name: string; module?: string}>): void {
    if (sortedClients.length === 0) {
      this.currentClientSubject.next(null);
      return;
    }

    // Try to restore previously selected client from current session
    const currentClientStr = sessionStorage.getItem('currentClient');
    if (currentClientStr) {
      try {
        const currentClient = JSON.parse(currentClientStr);
        // Verify the restored client still exists in the new clients list
        if (sortedClients.find(c => c.client_id === currentClient.client_id)) {
          this.currentClientSubject.next(currentClient);
          return;
        }
      } catch (err) {
      }
    }

    // Default to first client (already sorted alphabetically)
    this.currentClientSubject.next(sortedClients[0]);
    try {
      // Persist to sessionStorage only (for current session visibility)
      sessionStorage.setItem('currentClient', JSON.stringify(sortedClients[0]));
    } catch (err) {
    }
  }

  /**
   * Get access token silently for API calls
   * MSAL handles token caching and refresh automatically
   */
  getAccessToken(): Observable<AuthenticationResult> {
    const account = this.getActiveAccount();
    
    if (!account) {
      throw new Error('No active account. Please login first.');
    }

    const silentRequest: SilentRequest = {
      scopes: loginRequest.scopes,
      account: account
    };

    return this.msalService.acquireTokenSilent(silentRequest);
  }

  /**
   * Get access token as string (for backward compatibility)
   * Returns null if not authenticated
   */
  getAccessTokenString(): string | null {
    // MSAL stores tokens internally, this method checks if user is logged in
    // The actual token is obtained via acquireTokenSilent() or MsalInterceptor
    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }
    
    // Return a placeholder - actual token is handled by MsalInterceptor
    return 'MSAL_MANAGED_TOKEN';
  }

  /**
   * Get token info for display
   */
  getTokenInfo(): { userName: string; expiresAt: string; isExpired: boolean } | null {
    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }

    // Get ID token claims for expiry info
    const idTokenClaims = account.idTokenClaims as any;
    const expiryTime = idTokenClaims?.exp ? idTokenClaims.exp * 1000 : Date.now() + 3600000;

    return {
      userName: account.name || account.username || 'Unknown User',
      expiresAt: new Date(expiryTime).toLocaleString(),
      isExpired: Date.now() >= expiryTime
    };
  }

  /**
   * Clear tokens (logout alias for backward compatibility)
   */
  clearTokens(): void {
    this.logout();
  }

  /**
   * Check if we should intercept this URL for auth
   * Used by the custom interceptor (MSAL Interceptor handles this automatically)
   */
  shouldIntercept(url: string): boolean {
    return url.includes(environment.apiBaseUrl);
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}

